/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessResolveDirectoryReviewItem } from "../funcs/accessResolveDirectoryReviewItem.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ResolveDirectoryReviewItemResult } from "../models/components/resolvedirectoryreviewitemresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ResolveDirectoryReviewItemRequest,
  ResolveDirectoryReviewItemSecurity,
} from "../models/operations/resolvedirectoryreviewitem.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ResolveDirectoryReviewItemMutationVariables = {
  request: ResolveDirectoryReviewItemRequest;
  security?: ResolveDirectoryReviewItemSecurity | undefined;
  options?: RequestOptions;
};

export type ResolveDirectoryReviewItemMutationData =
  ResolveDirectoryReviewItemResult;

export type ResolveDirectoryReviewItemMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * resolveDirectoryReviewItem access
 *
 * @remarks
 * Decide one pending directory review item. approve on a new group or attribute value maps it to the given role set, creating new roles as applyDirectoryRoleSetup does; approve on a missing source removes its mappings. dismiss leaves a new source unmapped, or keeps a missing source's mappings. opt_out_key dismisses a new attribute value and stops raising new values of its key. approve checks version as applyDirectoryRoleSetup does and returns conflict if the source changed. An item that is no longer pending returns conflict.
 */
export function useResolveDirectoryReviewItemMutation(
  options?: MutationHookOptions<
    ResolveDirectoryReviewItemMutationData,
    ResolveDirectoryReviewItemMutationError,
    ResolveDirectoryReviewItemMutationVariables
  >,
): UseMutationResult<
  ResolveDirectoryReviewItemMutationData,
  ResolveDirectoryReviewItemMutationError,
  ResolveDirectoryReviewItemMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildResolveDirectoryReviewItemMutation(client, options),
    ...options,
  });
}

export function mutationKeyResolveDirectoryReviewItem(): MutationKey {
  return ["@gram/client", "access", "resolveDirectoryReviewItem"];
}

export function buildResolveDirectoryReviewItemMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ResolveDirectoryReviewItemMutationVariables,
  ) => Promise<ResolveDirectoryReviewItemMutationData>;
} {
  return {
    mutationKey: mutationKeyResolveDirectoryReviewItem(),
    mutationFn: function resolveDirectoryReviewItemMutationFn({
      request,
      security,
      options,
    }): Promise<ResolveDirectoryReviewItemMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessResolveDirectoryReviewItem(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
